import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { drinkAlcohol, ageFromBirth, type SpiritType } from "@/lib/drink-alcohol";
import {
  ArrowLeft,
  Users,
  Wine,
  Coins,
  CreditCard,
  Cake,
  Beer,
  FileDown,
} from "lucide-react";
import { downloadEventReportPdf } from "@/lib/event-report-pdf";

export const Route = createFileRoute("/harikaradmin/events-report/$eventId")({
  head: () => ({ meta: [{ title: "Etkinlik Raporu — Admin" }] }),
  component: EventReportDetail,
});

interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  ends_at: string | null;
  venue: string | null;
}

interface TicketRow {
  id: string;
  user_id: string;
  checked_in_at: string | null;
  order_id: string | null;
}

interface ProfileRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string | null;
  photo_url: string | null;
}

interface DrinkRow {
  id: string;
  name: string;
  price_kurus: number;
}

interface VoucherRow {
  user_id: string;
  drink_id: string;
  redeemed_at: string | null;
  order_id: string | null;
  created_at: string;
}

interface OrderRow {
  id: string;
  user_id: string;
  total_kurus: number;
  quantity: number;
  status: string;
  payment_method: string | null;
  event_id: string | null;
  ticket_type_id: string | null;
  created_at: string;
  paid_at: string | null;
  admin_notes: string | null;
  shopier_marked_paid_by: string | null;
}

interface TicketTypeRow {
  id: string;
  name: string;
  price_kurus: number;
}


interface TxRow {
  id: string;
  user_id: string;
  amount: number;
  type: string;
  order_id: string | null;
  package_id: string | null;
  description: string | null;
  created_at: string;
  performed_by: string | null;
}


async function fetchAll<T>(build: () => any, pageSize = 1000): Promise<T[]> {
  const all: T[] = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await build().range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data as T[]) ?? [];
    all.push(...rows);
    if (rows.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

function EventReportDetail() {
  const { eventId } = Route.useParams();
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventRow | null>(null);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileRow>>({});
  const [drinks, setDrinks] = useState<Record<string, DrinkRow>>({});
  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [txs, setTxs] = useState<TxRow[]>([]);
  const [ticketTypes, setTicketTypes] = useState<Record<string, TicketTypeRow>>({});
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) void navigate({ to: "/" });
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    (async () => {
      setBusy(true);
      const { data: ev } = await supabase
        .from("events")
        .select("id, name, starts_at, ends_at, venue")
        .eq("id", eventId)
        .maybeSingle();
      setEvent(ev as EventRow | null);

      // Event window for attributing purchases made without event_id link
      const evStart = new Date((ev as EventRow).starts_at).getTime();
      const rawEnd = (ev as EventRow).ends_at
        ? new Date((ev as EventRow).ends_at!).getTime()
        : evStart + 12 * 3600 * 1000;
      // Party can run well past ends_at — extend window so late-night drinks are counted
      const evEnd = Math.max(rawEnd, evStart + 18 * 3600 * 1000);
      const winFrom = new Date(evStart - 6 * 3600 * 1000).toISOString();
      const winTo = new Date(evEnd + 8 * 3600 * 1000).toISOString();
      const activityFrom = new Date(evStart - 12 * 3600 * 1000).toISOString();
      const activityTo = new Date(evStart + 12 * 3600 * 1000).toISOString();

      const [evTickets, evOrders] = await Promise.all([
        fetchAll<TicketRow>(() =>
          supabase.from("tickets").select("id, user_id, checked_in_at, order_id").eq("event_id", eventId),
        ),
        fetchAll<OrderRow>(() =>
          supabase
            .from("orders")
            .select("id, user_id, total_kurus, quantity, status, payment_method, event_id, ticket_type_id, created_at, paid_at, admin_notes, shopier_marked_paid_by")
            .eq("event_id", eventId),
        ),
      ]);
      setTickets(evTickets);
      setOrders(evOrders);

      // Ticket types for this event (to compute admin_grant door revenue)
      const tts = await fetchAll<TicketTypeRow>(() =>
        supabase.from("ticket_types").select("id, name, price_kurus").eq("event_id", eventId),
      );
      const ttMap: Record<string, TicketTypeRow> = {};
      for (const t of tts) ttMap[t.id] = t;
      setTicketTypes(ttMap);


      // Attendee/holder users for this event
      const eventUserIds = Array.from(
        new Set<string>([...evTickets.map((t) => t.user_id), ...evOrders.map((o) => o.user_id)]),
      );

      // Vouchers: (a) explicitly linked to this event OR
      //          (b) bought by an attendee/holder within the event day window (event_id may be NULL)
      const voucherResults = await Promise.all([
        fetchAll<VoucherRow>(() =>
          supabase
            .from("drink_vouchers")
            .select("user_id, drink_id, redeemed_at, order_id, created_at")
            .eq("event_id", eventId),
        ),
        eventUserIds.length
          ? fetchAll<VoucherRow>(() =>
              supabase
                .from("drink_vouchers")
                .select("user_id, drink_id, redeemed_at, order_id, created_at")
                .in("user_id", eventUserIds)
                .gte("created_at", winFrom)
                .lte("created_at", winTo),
            )
          : Promise.resolve([] as VoucherRow[]),
      ]);
      const voucherMap = new Map<string, VoucherRow>();
      for (const v of [...voucherResults[0], ...voucherResults[1]]) {
        const key = `${v.user_id}|${v.drink_id}|${v.created_at}|${v.order_id ?? ""}`;
        voucherMap.set(key, v);
      }
      const evVouchers = Array.from(voucherMap.values());
      setVouchers(evVouchers);

      // Users involved (recipients + granting admins)
      const userIds = Array.from(
        new Set<string>([
          ...evTickets.map((t) => t.user_id),
          ...evVouchers.map((v) => v.user_id),
          ...evOrders.map((o) => o.user_id),
          ...evOrders
            .filter((o) => o.payment_method === "admin_grant" && o.shopier_marked_paid_by)
            .map((o) => o.shopier_marked_paid_by as string),
        ]),
      );


      const [profs, drinkList] = await Promise.all([
        userIds.length
          ? fetchAll<ProfileRow>(() =>
              supabase
                .from("profiles")
                .select("id, first_name, last_name, email, birth_date, photo_url")
                .in("id", userIds),
            )
          : Promise.resolve([] as ProfileRow[]),
        fetchAll<DrinkRow>(() => supabase.from("drinks").select("id, name, price_kurus")),
      ]);
      const pMap: Record<string, ProfileRow> = {};
      for (const p of profs) pMap[p.id] = p;
      setProfiles(pMap);
      const dMap: Record<string, DrinkRow> = {};
      for (const d of drinkList) dMap[d.id] = d;
      setDrinks(dMap);

      // Point transactions:
      // - Event-user transactions feed attendee/J totals.
      // - All admin_adjust transactions in the activity window feed the admin summary,
      //   even if the recipient did not have a ticket/order on this event.
      const [eventUserTxs, adminActivityTxs] = await Promise.all([
        userIds.length
          ? fetchAll<TxRow>(() =>
              supabase
                .from("point_transactions")
                .select("id, user_id, amount, type, order_id, package_id, description, created_at, performed_by")
                .in("user_id", userIds),
            )
          : Promise.resolve([] as TxRow[]),
        fetchAll<TxRow>(() =>
          supabase
            .from("point_transactions")
            .select("id, user_id, amount, type, order_id, package_id, description, created_at, performed_by")
            .eq("type", "admin_adjust")
            .gt("amount", 0)
            .not("performed_by", "is", null)
            .gte("created_at", activityFrom)
            .lte("created_at", activityTo),
        ),
      ]);
      const txMap = new Map<string, TxRow>();
      for (const tx of [...eventUserTxs, ...adminActivityTxs]) txMap.set(tx.id, tx);
      const evTxs = Array.from(txMap.values());
      setTxs(evTxs);

      // Also fetch any admin profiles referenced by performed_by that we didn't already load.
      const extraAdminIds = Array.from(
        new Set(
          evTxs
            .map((t) => t.performed_by)
            .filter((v): v is string => !!v && !pMap[v]),
        ),
      );
      if (extraAdminIds.length) {
        const extras = await fetchAll<ProfileRow>(() =>
          supabase
            .from("profiles")
            .select("id, first_name, last_name, email, birth_date, photo_url")
            .in("id", extraAdminIds),
        );
        const merged: Record<string, ProfileRow> = { ...pMap };
        for (const p of extras) merged[p.id] = p;
        setProfiles(merged);
      }
      setBusy(false);

    })();
  }, [eventId, user, isAdmin]);

  const fmtTL = (k: number) =>
    `${(k / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`;

  const report = useMemo(() => {
    if (!event) return null;

    // Time window for attributing J loads to this event:
    // from 30 days before event start until 12 hours after start (or ends_at if set).
    const evStart = new Date(event.starts_at).getTime();
    const winFrom = evStart - 30 * 24 * 3600 * 1000;
    const evEnd = event.ends_at ? new Date(event.ends_at).getTime() : evStart + 12 * 3600 * 1000;

    // Attendees: unique users with a checked-in ticket
    const attendeeIds = new Set<string>();
    for (const t of tickets) if (t.checked_in_at) attendeeIds.add(t.user_id);
    // Fallback: also include ticket holders even if not scanned
    const holderIds = new Set<string>(tickets.map((t) => t.user_id));

    // Ticket order IDs (to distinguish ticket vs drink orders)
    const ticketOrderIds = new Set(
      orders.filter((o) => o.ticket_type_id !== null).map((o) => o.id),
    );

    // Drink stats per user
    const perUser = new Map<
      string,
      {
        drinksBought: number;
        drinksRedeemed: number;
        drinkSpendTL: number; // kurus
        alcoholCl: number;
        bySpirit: Record<SpiritType, number>;
        drinkNames: Map<string, number>; // drink name -> qty (bought)
        jLoadedAdmin: number;
        jLoadedShopier: number;
        jLoadedTicket: number; // auto from ticket payment (informational)
        jSpent: number;
        jEarned: number;
        tlSpentTickets: number; // kurus
        tlSpentDrinks: number; // kurus
      }
    >();
    const ensure = (uid: string) => {
      if (!perUser.has(uid))
        perUser.set(uid, {
          drinksBought: 0,
          drinksRedeemed: 0,
          drinkSpendTL: 0,
          alcoholCl: 0,
          bySpirit: {
            gin: 0, vodka: 0, tequila: 0, whiskey: 0, jager: 0, beer: 0, other: 0, none: 0,
          },
          drinkNames: new Map(),
          jLoadedAdmin: 0,
          jLoadedShopier: 0,
          jLoadedTicket: 0,
          jSpent: 0,
          jEarned: 0,
          tlSpentTickets: 0,
          tlSpentDrinks: 0,
        });
      return perUser.get(uid)!;
    };

    // Vouchers → drinks (alkol miktarı satın alınana göre hesaplanır)
    for (const v of vouchers) {
      const drink = drinks[v.drink_id];
      if (!drink) continue;
      const u = ensure(v.user_id);
      u.drinksBought += 1;
      u.drinkSpendTL += drink.price_kurus;
      u.drinkNames.set(drink.name, (u.drinkNames.get(drink.name) ?? 0) + 1);
      const info = drinkAlcohol(drink.name);
      u.alcoholCl += info.cl;
      u.bySpirit[info.spirit] += info.cl;
      if (v.redeemed_at) {
        u.drinksRedeemed += 1;
      }
    }

    // Orders → TL spent (paid only). For admin_grant tickets, treat as
    // door revenue at the ticket type's normal price × quantity — but skip
    // free tickets (price = 0).
    for (const o of orders) {
      if (o.status !== "paid") continue;
      const u = ensure(o.user_id);
      if (o.payment_method === "admin_grant") {
        if (!o.ticket_type_id) continue;
        const tt = ticketTypes[o.ticket_type_id];
        const price = tt?.price_kurus ?? 0;
        if (price <= 0) continue; // free / davetiye — ignore
        u.tlSpentTickets += price * (o.quantity ?? 0);
      } else {
        if (o.ticket_type_id) u.tlSpentTickets += o.total_kurus;
        else u.tlSpentDrinks += o.total_kurus;
      }
    }

    // Point transactions in window
    for (const t of txs) {
      if (!attendeeIds.has(t.user_id) && !holderIds.has(t.user_id)) continue;
      const ts = new Date(t.created_at).getTime();
      if (ts < winFrom || ts > evEnd) continue;
      const u = ensure(t.user_id);
      if (t.type === "spend" && t.amount < 0) u.jSpent += -t.amount;
      else if (t.type === "earn" && t.amount > 0) u.jEarned += t.amount;
      else if (t.type === "admin_adjust" && t.amount > 0) u.jLoadedAdmin += t.amount;
      else if (t.type === "purchase" && t.amount > 0) {
        // "purchase" is used both for Shopier J-package top-ups AND for
        // ticket-payment auto-credits (via credit_event_payment_as_points).
        // Only package_id is a real Shopier J-package purchase.
        if (t.package_id) u.jLoadedShopier += t.amount;
        // Only count ticket-payment auto-credits for THIS event's orders.
        // Without this filter, unrelated ticket purchases from other events
        // (within the 30-day window) leak into the total.
        else if (t.order_id && ticketOrderIds.has(t.order_id)) u.jLoadedTicket += t.amount;
      }
    }

    // Attendee rows
    const rows = Array.from(new Set<string>([...attendeeIds, ...holderIds])).map((uid) => {
      const p = profiles[uid];
      const stats = perUser.get(uid);
      const age = p?.birth_date ? ageFromBirth(p.birth_date) : null;
      return {
        user_id: uid,
        profile: p,
        checked_in: attendeeIds.has(uid),
        age,
        stats,
      };
    });

    rows.sort((a, b) => {
      if (a.checked_in !== b.checked_in) return a.checked_in ? -1 : 1;
      const an = `${a.profile?.first_name ?? ""} ${a.profile?.last_name ?? ""}`;
      const bn = `${b.profile?.first_name ?? ""} ${b.profile?.last_name ?? ""}`;
      return an.localeCompare(bn, "tr");
    });

    // Aggregates
    const checkedInAges = rows.filter((r) => r.checked_in && r.age != null).map((r) => r.age!);
    const avgAge =
      checkedInAges.length > 0
        ? checkedInAges.reduce((s, a) => s + a, 0) / checkedInAges.length
        : null;

    const totals = {
      attendees: attendeeIds.size,
      ticketHolders: holderIds.size,
      drinksBought: vouchers.length,
      drinksRedeemed: vouchers.filter((v) => v.redeemed_at).length,
      alcoholCl: 0,
      bySpirit: {
        gin: 0, vodka: 0, tequila: 0, whiskey: 0, jager: 0, beer: 0, other: 0, none: 0,
      } as Record<SpiritType, number>,
      jLoadedAdmin: 0,
      jLoadedShopier: 0,
      jLoadedTicket: 0,
      jSpent: 0,
      jEarned: 0,
      tlTickets: 0,
      tlDrinks: 0,
      drinkNameTotals: new Map<string, number>(),
    };
    for (const [, s] of perUser) {
      totals.alcoholCl += s.alcoholCl;
      for (const k of Object.keys(s.bySpirit) as SpiritType[]) totals.bySpirit[k] += s.bySpirit[k];
      totals.jLoadedAdmin += s.jLoadedAdmin;
      totals.jLoadedShopier += s.jLoadedShopier;
      totals.jLoadedTicket += s.jLoadedTicket;
      totals.jSpent += s.jSpent;
      totals.jEarned += s.jEarned;
      totals.tlTickets += s.tlSpentTickets;
      totals.tlDrinks += s.tlSpentDrinks;
      for (const [nm, q] of s.drinkNames)
        totals.drinkNameTotals.set(nm, (totals.drinkNameTotals.get(nm) ?? 0) + q);
    }

    // Admin-granted tickets for this event (only real admin grants — exclude
    // self-claimed invite tickets which also use payment_method='admin_grant').
    const grantedTickets = orders
      .filter(
        (o) =>
          o.payment_method === "admin_grant" &&
          o.ticket_type_id &&
          o.shopier_marked_paid_by,
      )

      .map((o) => {
        const tt = o.ticket_type_id ? ticketTypes[o.ticket_type_id] : null;
        return {
          order_id: o.id,
          recipient: profiles[o.user_id] ?? null,
          recipient_id: o.user_id,
          quantity: o.quantity ?? 0,
          ticket_type_name: tt?.name ?? "—",
          price_kurus: tt?.price_kurus ?? 0,
          notes: o.admin_notes,
          granted_by: o.shopier_marked_paid_by
            ? profiles[o.shopier_marked_paid_by] ?? null
            : null,
          granted_at: o.paid_at ?? o.created_at,
        };
      })
      .sort(
        (a, b) => new Date(b.granted_at).getTime() - new Date(a.granted_at).getTime(),
      );

    // Admin activity summary for this event: tickets granted + J loaded (admin_adjust)
    // within a ±12h window around the event start.
    const evStartMs = new Date(event.starts_at).getTime();
    const activityFrom = evStartMs - 12 * 3600 * 1000;
    const activityTo = evStartMs + 12 * 3600 * 1000;

    const adminAgg = new Map<
      string,
      {
        adminId: string;
        ticketRecipients: Set<string>;
        ticketQty: number;
        jRecipients: Set<string>;
        jTotal: number;
      }
    >();
    const ensureAdmin = (id: string) => {
      let a = adminAgg.get(id);
      if (!a) {
        a = {
          adminId: id,
          ticketRecipients: new Set<string>(),
          ticketQty: 0,
          jRecipients: new Set<string>(),
          jTotal: 0,
        };
        adminAgg.set(id, a);
      }
      return a;
    };

    for (const g of grantedTickets) {
      if (!g.granted_by) continue;
      const a = ensureAdmin(g.granted_by.id);
      a.ticketRecipients.add(g.recipient_id);
      a.ticketQty += g.quantity;
    }

    for (const t of txs) {
      if (t.type !== "admin_adjust" || t.amount <= 0 || !t.performed_by) continue;
      const ts = new Date(t.created_at).getTime();
      if (ts < activityFrom || ts > activityTo) continue;
      const a = ensureAdmin(t.performed_by);
      a.jRecipients.add(t.user_id);
      a.jTotal += t.amount;
    }


    const adminActivity = Array.from(adminAgg.values())
      .map((a) => ({
        adminId: a.adminId,
        admin: profiles[a.adminId] ?? null,
        ticketPeople: a.ticketRecipients.size,
        ticketQty: a.ticketQty,
        jPeople: a.jRecipients.size,
        jTotal: a.jTotal,
      }))
      .sort((a, b) => b.ticketQty + b.jTotal / 100 - (a.ticketQty + a.jTotal / 100));

    return { rows, totals, avgAge, grantedTickets, adminActivity };

  }, [event, tickets, vouchers, orders, txs, profiles, drinks, ticketTypes]);

  if (loading || !user || !isAdmin || busy || !event || !report) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-6xl px-4 py-10 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  const { rows, totals, avgAge, grantedTickets, adminActivity } = report;


  const drinkNameEntries = Array.from(totals.drinkNameTotals.entries()).sort(
    (a, b) => b[1] - a[1],
  );

  const spiritOrder: SpiritType[] = ["gin", "vodka", "tequila", "whiskey", "jager", "beer", "other"];
  const spiritLabels: Record<SpiritType, string> = {
    gin: "Cin", vodka: "Vodka", tequila: "Tekila", whiskey: "Viski", jager: "Jäger", beer: "Bira", other: "Diğer", none: "—",
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <Link
          to="/harikaradmin/events-report"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Etkinlik raporları
        </Link>
        <h1 className="mb-2 text-3xl font-bold tracking-tight">{event.name}</h1>
        <p className="mb-6 text-muted-foreground">
          {new Date(event.starts_at).toLocaleString("tr-TR", {
            dateStyle: "long",
            timeStyle: "short",
          })}
          {event.venue ? ` — ${event.venue}` : ""}
        </p>
        <AdminTabs />

        <div className="mb-6 flex justify-end">
          <button
            type="button"
            onClick={async () => {
              try {
                await downloadEventReportPdf({
                  event: {
                    name: event.name,
                    starts_at: event.starts_at,
                    venue: event.venue,
                  },
                  rows,
                  totals,
                  avgAge,
                });
              } catch (err) {
                console.error(err);
                alert("PDF oluşturulamadı");
              }
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-card transition-colors hover:bg-primary/90"
          >
            <FileDown className="h-4 w-4" /> PDF olarak indir
          </button>
        </div>

        <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Katılım (giriş)" value={String(totals.attendees)} sub={`${totals.ticketHolders} bilet sahibi`} icon={Users} />
          <StatCard label="Yaş ortalaması" value={avgAge != null ? avgAge.toFixed(1) : "—"} sub="Giriş yapanlar" icon={Cake} />
          <StatCard label="İçecek (kullanılan)" value={String(totals.drinksRedeemed)} sub={`${totals.drinksBought} satın alınan`} icon={Wine} />
          <StatCard label="Toplam alkol" value={`${totals.alcoholCl.toFixed(1)} cl`} sub="Kullanılan içeceklere göre" icon={Beer} />
        </section>

        <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="J admin yükleme" value={`${totals.jLoadedAdmin.toLocaleString("tr-TR")} J`} icon={Coins} />
          <StatCard label="J Shopier satın alma" value={`${totals.jLoadedShopier.toLocaleString("tr-TR")} J`} sub={`Biletle otomatik: ${totals.jLoadedTicket.toLocaleString("tr-TR")} J`} icon={Coins} />
          <StatCard label="J harcanan" value={`${totals.jSpent.toLocaleString("tr-TR")} J`} icon={Coins} />
          <StatCard label="Bilet ödemeleri" value={fmtTL(totals.tlTickets)} sub={`İçecek: ${fmtTL(totals.tlDrinks)}`} icon={CreditCard} />
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <Beer className="h-5 w-5 text-primary" /> Alkol dağılımı
          </h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {spiritOrder.map((k) => (
              <div key={k} className="rounded-xl bg-muted/40 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {spiritLabels[k]}
                </div>
                <div className="mt-1 text-xl font-semibold">
                  {(totals.bySpirit[k] / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                  <span className="text-sm font-normal text-muted-foreground">L</span>
                </div>
                <div className="text-xs text-muted-foreground">{totals.bySpirit[k].toFixed(1)} cl</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between rounded-xl bg-primary/10 p-3">
            <span className="text-sm font-medium">Toplam</span>
            <span className="text-lg font-bold">
              {(totals.alcoholCl / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L
              <span className="ml-2 text-xs font-normal text-muted-foreground">({totals.alcoholCl.toFixed(1)} cl)</span>
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Kokteyller 6.5 cl, Gin+Soda / Gin+Tonic 7.5 cl, Black Label (double) 7.5 cl, shot'lar 4 cl, bira 30 cl olarak hesaplanır.
          </p>
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <Wine className="h-5 w-5 text-primary" /> İçecek dökümü
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">İçecek</th>
                  <th className="py-2 px-3 text-right">Satılan</th>
                </tr>
              </thead>
              <tbody>
                {drinkNameEntries.map(([nm, q]) => (
                  <tr key={nm} className="border-b border-border/50">
                    <td className="py-2 pr-3">{nm}</td>
                    <td className="py-2 px-3 text-right font-medium">{q}</td>
                  </tr>
                ))}
                {drinkNameEntries.length === 0 && (
                  <tr>
                    <td colSpan={2} className="py-6 text-center text-muted-foreground">
                      İçecek satışı yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <Users className="h-5 w-5 text-primary" /> Admin tarafından verilen biletler ({grantedTickets.reduce((s, g) => s + g.quantity, 0)})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Alıcı</th>
                  <th className="py-2 px-2">Bilet türü</th>
                  <th className="py-2 px-2 text-right">Adet</th>
                  <th className="py-2 px-2 text-right">Birim</th>
                  <th className="py-2 px-2">Not</th>
                  <th className="py-2 px-2">Veren</th>
                  <th className="py-2 pl-2">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {grantedTickets.map((g) => {
                  const r = g.recipient;
                  const a = g.granted_by;
                  return (
                    <tr key={g.order_id} className="border-b border-border/50 align-top">
                      <td className="py-2 pr-3">
                        {r ? (
                          <Link
                            to="/harikaradmin/members/$userId"
                            params={{ userId: r.id }}
                            className="font-medium hover:underline"
                          >
                            {r.first_name} {r.last_name}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {r?.email ? (
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        ) : null}
                      </td>
                      <td className="py-2 px-2">{g.ticket_type_name}</td>
                      <td className="py-2 px-2 text-right font-medium">{g.quantity}</td>
                      <td className="py-2 px-2 text-right">
                        {g.price_kurus > 0 ? fmtTL(g.price_kurus) : (
                          <span className="text-muted-foreground">Ücretsiz</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground whitespace-pre-wrap max-w-[220px]">
                        {g.notes || "—"}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {a ? `${a.first_name} ${a.last_name}` : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 pl-2 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(g.granted_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                    </tr>
                  );
                })}
                {grantedTickets.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-muted-foreground">
                      Bu etkinlikte admin tarafından verilen bilet yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold">
            <Users className="h-5 w-5 text-primary" /> Admin aktivite özeti
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            J yüklemeleri etkinlik başlangıcının ±12 saati içinde sayılır.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Admin</th>
                  <th className="py-2 px-2 text-right">Bilet verilen kişi</th>
                  <th className="py-2 px-2 text-right">Bilet adet</th>
                  <th className="py-2 px-2 text-right">J yüklenen kişi</th>
                  <th className="py-2 pl-2 text-right">Toplam J</th>
                </tr>
              </thead>
              <tbody>
                {adminActivity.map((a) => (
                  <tr key={a.adminId} className="border-b border-border/50">
                    <td className="py-2 pr-3">
                      {a.admin ? (
                        <span className="font-medium">
                          {a.admin.first_name} {a.admin.last_name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {a.admin?.email ? (
                        <div className="text-xs text-muted-foreground">{a.admin.email}</div>
                      ) : null}
                    </td>
                    <td className="py-2 px-2 text-right">{a.ticketPeople}</td>
                    <td className="py-2 px-2 text-right font-medium">{a.ticketQty}</td>
                    <td className="py-2 px-2 text-right">{a.jPeople}</td>
                    <td className="py-2 pl-2 text-right font-medium">
                      {a.jTotal.toLocaleString("tr-TR")} J
                    </td>
                  </tr>
                ))}
                {adminActivity.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-muted-foreground">
                      Bu etkinlikte admin aktivitesi yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>




        <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <Users className="h-5 w-5 text-primary" /> Katılımcılar ({rows.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Üye</th>
                  <th className="py-2 px-2 text-right">Yaş</th>
                  <th className="py-2 px-2 text-right">İçecek</th>
                  <th className="py-2 px-2 text-right">Alkol (cl)</th>
                  <th className="py-2 px-2 text-right">J yüklenen (admin)</th>
                  <th className="py-2 px-2 text-right">J alınan (Shopier)</th>
                  <th className="py-2 px-2 text-right">J harcanan</th>
                  <th className="py-2 px-2 text-right">Harcama</th>
                  <th className="py-2 pl-2 text-right">Durum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const p = r.profile;
                  const s = r.stats;
                  return (
                    <tr key={r.user_id} className="border-b border-border/50">
                      <td className="py-2 pr-3">
                        {p ? (
                          <Link
                            to="/harikaradmin/members/$userId"
                            params={{ userId: p.id }}
                            className="inline-flex items-center gap-2 hover:underline"
                          >
                            {p.photo_url ? (
                              <img
                                src={p.photo_url}
                                alt=""
                                className="h-6 w-6 rounded-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                                {p.first_name?.[0]}
                                {p.last_name?.[0]}
                              </div>
                            )}
                            <span className="font-medium">
                              {p.first_name} {p.last_name}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">{r.age ?? "—"}</td>
                      <td className="py-2 px-2 text-right">
                        {s ? `${s.drinksRedeemed}/${s.drinksBought}` : "0"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {s ? s.alcoholCl.toFixed(1) : "0"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {s ? `${s.jLoadedAdmin} J` : "0 J"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {s ? `${s.jLoadedShopier} J` : "0 J"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {s ? `${s.jSpent} J` : "0 J"}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {s ? fmtTL(s.tlSpentTickets + s.tlSpentDrinks) : "—"}
                      </td>
                      <td className="py-2 pl-2 text-right">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            r.checked_in
                              ? "bg-green-500/15 text-green-600 dark:text-green-400"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {r.checked_in ? "Giriş yaptı" : "Bilet aldı"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-6 text-center text-muted-foreground">
                      Katılımcı yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}
